import {
  Autocomplete,
  AutocompleteItem,
  Button,
  Checkbox,
  DatePicker,
  DateValue,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  useDisclosure,
} from "@heroui/react";
import { useState, useEffect } from "react";
import { FaPlus, FaRubleSign } from "react-icons/fa6";
import { NewRenterHandbook } from "../../handbooks/renterHandbook";
import { useForm, Controller } from "react-hook-form";
import { useAppSelector } from "@/hooks/useAppSelector.ts";
import { api } from "@/api";
import { toast } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import {
  getIdFromOfficeHandbook,
  getIdFromRenterHandbook,
  getIdFromRequisitesHandbook,
} from "@/utils/getIdFromHandbooks.ts";
import { NewRequisitesHandbook } from "../../handbooks/requisitesHandbook";
import { CodependentOfficeHandbook } from "../../handbooks/officeHandbook";
import { getMonthInRussian } from "@/utils";
import { parseDate } from "@internationalized/date";
import { I18nProvider } from "@react-aria/i18n";
import { Agreement } from "@/api/agreements";
import { NewYearHandbook } from "../../handbooks/service/yearHandbook";
import { SendBillModal } from "./sendBillPrompt";
import { CodependentRenterRequisitesHandbook } from "../../handbooks/renterRequisitesHandbook";
import { NewBillingTypeHandbook } from "../../handbooks/service/billingTypeHandbook";
import { CodependentAgreementHandbook } from "../../handbooks/agreementHandbook";

const months = [
  { key: "Январь", label: "Январь" },
  { key: "Февраль", label: "Февраль" },
  { key: "Март", label: "Март" },
  { key: "Апрель", label: "Апрель" },
  { key: "Май", label: "Май" },
  { key: "Июнь", label: "Июнь" },
  { key: "Июль", label: "Июль" },
  { key: "Август", label: "Август" },
  { key: "Сентябрь", label: "Сентябрь" },
  { key: "Октябрь", label: "Октябрь" },
  { key: "Ноябрь", label: "Ноябрь" },
  { key: "Декабрь", label: "Декабрь" },
];

interface CreateBillForm {
  number: string;
  date: DateValue;
  payer: number;
  provider: number;
  clientProvider: number;
  year: string;
  office: number;
  discount?: number;
  period: string;
  amount: number;
  paid: boolean;
  description?: string;
  type: number;
  agreement_id: number;
}

export function CreateBillFromFinanceModal({
  disclosure,
  paymentProps,
}: {
  disclosure: ReturnType<typeof useDisclosure>;
  paymentProps: {
    rentedOffice: Agreement | null;
    month: number;
    customPrice?: number;
    year: string;
    type?: "payment" | "deposit";
    targetOffice?: number;
  };
}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [finalAmount, setFinalAmount] = useState<number>(0);
  const [officePrice, setOfficePrice] = useState<number | null>(null);
  const [paymentId, setPaymentId] = useState<number | null>(null);
  const [renterId, setRenterId] = useState<number | null>(null);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CreateBillForm>({
    defaultValues: {
      amount: paymentProps.customPrice || 0,
      discount: 0,
      number: "0",
      paid: false,
    },
  });

  const offices = useAppSelector((state) => state.handbooks.office);
  const requisites = useAppSelector((state) => state.handbooks.requisites);

  const handleError = useErrorHandling();

  const sendBillDisclosure = useDisclosure();

  const watchAmount = watch("amount", 0);
  const watchDiscount = watch("discount", 0);
  const watchAgreementId = watch("agreement_id", 0);

  const calculateFinalAmount = (amount: number, discount: number) => {
    const discountAmount = (amount * discount) / 100;
    const finalSum = amount - discountAmount;
    return finalSum > 0 ? finalSum : 0;
  };

  useEffect(() => {
    setFinalAmount(calculateFinalAmount(watchAmount, watchDiscount || 0));
  }, [watchAmount, watchDiscount]);

  const onHandle = async (data: CreateBillForm) => {
    setLoading(true);
    console.log(data);

    const payer = await getIdFromRenterHandbook(String(data.payer));
    const provider = await getIdFromRequisitesHandbook(String(data.provider));
    const office = await getIdFromOfficeHandbook(String(data.office));

    api.billings
      .create({
        amount: data.amount,
        date: data.date.toString(),
        description: data.description || "",
        discount: 0,
        number: String(data.number),
        paid: !!data.paid || false,
        year: data.year,
        payer: Number(payer),
        period: data.period,
        provider: Number(provider) || null,
        office_id: Number(office),
        clientProvider: Number(data.clientProvider) || null,
        type: Number(data.type),
        agreement_id: Number(data.agreement_id),
      })
      .then((_data) => {
        toast.success("Счёт успешно создан!");
        disclosure.onClose();

        const requisite = requisites.find((r) => r.id === Number(provider));
        if (requisite?.billable) {
          setPaymentId(_data.data.id);
          setRenterId(_data.data.payer);
          sendBillDisclosure.onOpen();
        }
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onOfficeChange = (office: number) => {
    const foundOffice = offices.find((o) => o.id === office);
    const finalPrice = paymentProps.customPrice || foundOffice?.price || 0;

    setOfficePrice(finalPrice);
    setValue("amount", finalPrice);
    setFinalAmount(finalPrice);
  };

  useEffect(() => {
    const agreementOfficeId =
      paymentProps.targetOffice ||
      paymentProps.rentedOffice?.offices?.[0].officeId;
    const foundOffice = offices.find((o) => o.id === agreementOfficeId);

    reset({
      number: (paymentProps.month + 1).toString() + foundOffice?.display_name,
      period: getMonthInRussian(paymentProps.month),
      // @ts-ignore
      provider: String(paymentProps.rentedOffice?.requisite),
      payer: paymentProps.rentedOffice?.renterId,
      year: paymentProps.year,
      office: agreementOfficeId ?? 0,
      date: parseDate(new Date().toISOString().split("T")[0]),
      // @ts-ignore
      agreement_id: String(paymentProps.rentedOffice?.id),
      amount: 0,
      discount: 0,
      type: paymentProps.type == "payment" ? 1 : 2,
    });

    onOfficeChange(agreementOfficeId ?? 0);
    setFinalAmount(0);
    setLoading(false);
  }, [disclosure.isOpen, reset, paymentProps]);

  return (
    <>
      <SendBillModal
        disclosure={sendBillDisclosure}
        paymentId={paymentId!}
        renterId={renterId!}
      />
      <Modal
        isOpen={disclosure.isOpen}
        onOpenChange={disclosure.onOpenChange}
        portalContainer={document.getElementById("main")!}
        backdrop="blur"
        scrollBehavior="outside"
        isDismissable={false}
        classNames={{
          backdrop: "z-[500]",
          wrapper: "z-[500]",
        }}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Создание счёта</ModalHeader>
              <form onSubmit={handleSubmit(onHandle)}>
                <ModalBody className="flex flex-col gap-4">
                  <div className="flex flex-row gap-2">
                    <Controller
                      name="number"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          label="Счёт"
                          placeholder="Номер счёта с бухгалтерии"
                          isRequired
                          isInvalid={!!errors["number"]}
                          errorMessage={
                            errors["number"]?.message?.toString() || ""
                          }
                          variant="bordered"
                          value={(field.value ?? 0).toString()}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="date"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <I18nProvider locale="ru-RU">
                          <DatePicker
                            label="Дата платежа"
                            variant="bordered"
                            isRequired
                            value={field.value}
                            onChange={field.onChange}
                            isInvalid={!!errors["date"]}
                            errorMessage={
                              errors["date"]?.message?.toString() || ""
                            }
                          />
                        </I18nProvider>
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="period"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Autocomplete
                          label="Оплата за месяц"
                          placeholder="Выберите месяц"
                          variant="bordered"
                          isRequired
                          isDisabled
                          isInvalid={!!errors["period"]}
                          selectedKey={field.value}
                          onSelectionChange={(key) => field.onChange(key)}>
                          {months.map((month) => (
                            <AutocompleteItem key={month.key}>
                              {month.label}
                            </AutocompleteItem>
                          ))}
                        </Autocomplete>
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="year"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <NewYearHandbook
                          isRequired
                          isInvalid={!!errors["year"]}
                          errorMessage={
                            errors["year"]?.message?.toString() || ""
                          }
                          selectedKey={field.value}
                          onSelectionChange={(key) => field.onChange(key)}
                          children={<></>}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="provider"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <NewRequisitesHandbook
                          isRequired
                          isInvalid={!!errors["provider"]}
                          errorMessage={
                            errors["provider"]?.message?.toString() || ""
                          }
                          selectedKey={field.value}
                          onSelectionChange={(key) => field.onChange(key)}
                          children={<></>}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="payer"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <NewRenterHandbook
                          isRequired
                          isInvalid={!!errors["payer"]}
                          errorMessage={
                            errors["payer"]?.message?.toString() || ""
                          }
                          isDisabled
                          selectedKey={String(field.value)}
                          onSelectionChange={(key) => field.onChange(key)}
                          children={<></>}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="clientProvider"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <CodependentRenterRequisitesHandbook
                          isRequired
                          deps={[
                            (paymentProps.rentedOffice?.renterId || 0).toString(),
                          ]}
                          isInvalid={!!errors["clientProvider"]}
                          errorMessage={
                            errors["clientProvider"]?.message?.toString() || ""
                          }
                          selectedKey={field.value}
                          onSelectionChange={(key) => field.onChange(key)}
                          children={<></>}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="office"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <CodependentOfficeHandbook
                          isInvalid={!!errors["office"]}
                          isRequired
                          deps={[String(watchAgreementId || 0)]}
                          errorMessage={
                            errors["office"]?.message?.toString() || ""
                          }
                          selectedKey={String(field.value)}
                          onSelectionChange={(key) => {
                            field.onChange(key);
                            onOfficeChange(Number(key));
                          }}
                          children={<></>}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Controller
                      name="agreement_id"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <CodependentAgreementHandbook
                          isRequired
                          deps={[String(paymentProps.rentedOffice?.renterId)]}
                          isInvalid={!!errors["agreement_id"]}
                          errorMessage={
                            errors["agreement_id"]?.message?.toString() || ""
                          }
                          selectedKey={field.value}
                          onSelectionChange={(key) => {
                            field.onChange(key);
                          }}
                          children={<></>}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-row gap-2">
                    <Controller
                      name="amount"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          className="w-[60%]"
                          label="Сумма"
                          placeholder="Сумма без учёта скидки"
                          isRequired
                          isInvalid={!!errors["amount"]}
                          errorMessage={
                            errors["amount"]?.message?.toString() || ""
                          }
                          variant="bordered"
                          value={(field.value ?? 0).toString()}
                          onValueChange={field.onChange}
                        />
                      )}
                    />
                    <div className="flex flex-row justify-center items-center w-[40%]">
                      {officePrice !== null ? (
                        <span
                          className="cursor-pointer border-b-2 border-primary-500"
                          onClick={(_e) => setValue("amount", officePrice)}>
                          {officePrice.toLocaleString()} ₽
                        </span>
                      ) : (
                        <span className="text-foreground text-opacity-30 text-sm">
                          Выберите офис
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-row gap-2">
                    <Controller
                      name="type"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <NewBillingTypeHandbook
                          isRequired
                          isInvalid={!!errors["type"]}
                          errorMessage={
                            errors["type"]?.message?.toString() || ""
                          }
                          selectedKey={field.value}
                          onSelectionChange={(key) => {
                            field.onChange(key);
                          }}
                          children={<></>}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-row gap-2">
                    <Controller
                      name="description"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Описание"
                          placeholder="Описание к счёту"
                          isInvalid={!!errors["description"]}
                          errorMessage={
                            errors["description"]?.message?.toString() || ""
                          }
                          variant="bordered"
                          value={field.value || ""}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  <div className="flex flex-row gap-2">
                    <Controller
                      name="paid"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          isSelected={field.value}
                          onChange={field.onChange}>
                          Оплачено
                        </Checkbox>
                      )}
                    />
                  </div>
                  <div className="flex flex-row gap-2 items-center">
                    <span className="text-foreground text-opacity-50 font-medium">
                      Итоговая сумма:
                    </span>
                    <div className="flex flex-row items-center gap-1">
                      <span className="font-bold text-lg">
                        {finalAmount.toLocaleString()}
                      </span>
                      <FaRubleSign className="text-lg" />
                    </div>
                  </div>
                </ModalBody>
                <ModalFooter>
                  <div className="flex flex-grow flex-row gap-2">
                    <Button color="default" onClick={onClose} variant="flat">
                      Закрыть
                    </Button>
                  </div>
                  <Button
                    color="success"
                    type="submit"
                    variant="flat"
                    startContent={<FaPlus />}
                    isLoading={loading}>
                    Создать
                  </Button>
                </ModalFooter>
              </form>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
